export type RgbTriple = readonly [number, number, number];

const rgbCache = new Map<string, RgbTriple>();

function parseNumberToken(token: string): number {
  const n = Number(token.replace(/deg$/i, ""));
  if (!Number.isFinite(n)) {
    throw new Error(`Unsupported colour component: ${token}`);
  }
  return n;
}

function parseLightness(token: string): number {
  if (token.endsWith("%")) return parseNumberToken(token.slice(0, -1)) / 100;
  return parseNumberToken(token);
}

function parseRgbChannel(token: string): number {
  if (token.endsWith("%")) {
    return Math.round((parseNumberToken(token.slice(0, -1)) / 100) * 255);
  }
  return Math.round(parseNumberToken(token));
}

function toSrgbChannel(linear: number): number {
  const clamped = Math.min(1, Math.max(0, linear));
  const encoded =
    clamped <= 0.0031308
      ? 12.92 * clamped
      : 1.055 * clamped ** (1 / 2.4) - 0.055;
  return Math.round(encoded * 255);
}

export function parseCssColorToRgb(value: string): RgbTriple {
  const colour = value.trim();
  const rgb = /^rgba?\((.+)\)$/i.exec(colour);
  if (rgb) {
    const channels = rgb[1]!
      .replace(/\s*\/\s*.+$/, "")
      .replace(/,/g, " ")
      .trim()
      .split(/\s+/);
    if (channels.length < 3) {
      throw new Error(`Unsupported rgb colour token: ${value}`);
    }
    return [
      parseRgbChannel(channels[0]!),
      parseRgbChannel(channels[1]!),
      parseRgbChannel(channels[2]!),
    ];
  }

  const oklch = /^oklch\((.+)\)$/i.exec(colour);
  if (!oklch) {
    throw new Error(`Unsupported CSS colour token: ${value}`);
  }

  const parts = oklch[1]!
    .replace(/\s*\/\s*.+$/, "")
    .trim()
    .split(/\s+/);
  if (parts.length < 3) {
    throw new Error(`Unsupported OKLCH colour token: ${value}`);
  }

  const L = parseLightness(parts[0]!);
  const C = parseNumberToken(parts[1]!);
  const h = parseNumberToken(parts[2]!);
  const radians = (h * Math.PI) / 180;
  const a = C * Math.cos(radians);
  const b = C * Math.sin(radians);

  const lPrime = L + 0.3963377774 * a + 0.2158037573 * b;
  const mPrime = L - 0.1055613458 * a - 0.0638541728 * b;
  const sPrime = L - 0.0894841775 * a - 1.291485548 * b;

  const l = lPrime ** 3;
  const m = mPrime ** 3;
  const s = sPrime ** 3;

  return [
    toSrgbChannel(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    toSrgbChannel(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    toSrgbChannel(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ];
}

export function resolveAccentRgb(
  cssVarName: "--violet" | "--yellow",
  root: Element,
): RgbTriple {
  const raw = getComputedStyle(root).getPropertyValue(cssVarName).trim();
  if (!raw) {
    throw new Error(`Missing CSS colour token: ${cssVarName}`);
  }
  const cacheKey = `${cssVarName}:${raw}`;
  const cached = rgbCache.get(cacheKey);
  if (cached) return cached;
  const rgb = parseCssColorToRgb(raw);
  rgbCache.set(cacheKey, rgb);
  return rgb;
}
